import React, { useEffect, useRef, useState } from 'react';

const GlobeAnimation = () => {
  const containerRef = useRef(null);
  const worldRef = useRef(null);
  const [scaleFactor, setScaleFactor] = useState(1);

  // Country & Region Center Hubs
  const countryHubs = [
    { lat: 38.5, lng: -96.0, name: "United States" },
    { lat: 24.5, lng: 45.0, name: "Middle East" },
    { lat: 2.0, lng: 20.0, name: "Africa" },
    { lat: 20.6, lng: 78.9, name: "India" },
    { lat: 35.8, lng: 104.2, name: "China" }
  ];

  // Intercontinental Flight & Trade Routes
  const flightRoutes = [
    { startLat: 38.5, startLng: -96.0, endLat: 24.5, endLng: 45.0 },  // US -> Middle East
    { startLat: 24.5, startLng: 45.0, endLat: 20.6, endLng: 78.9 },   // Middle East -> India
    { startLat: 20.6, startLng: 78.9, endLat: 35.8, endLng: 104.2 },  // India -> China
    { startLat: 35.8, startLng: 104.2, endLat: 38.5, endLng: -96.0 }, // China -> US
    { startLat: 35.8, startLng: 104.2, endLat: 24.5, endLng: 45.0 },  // China -> Middle East
    { startLat: 24.5, startLng: 45.0, endLat: 2.0, endLng: 20.0 }     // Middle East -> Africa
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      // Smoothly enlarge globe up to 1.8x as user scrolls down
      const newScale = 1 + Math.min(0.8, scrollY / 500);
      setScaleFactor(newScale);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let animId;

    const createPlaneMesh = () => {
      const THREE = window.THREE;
      if (!THREE) return null;

      const group = new THREE.Group();

      // Fuselage / Body
      const bodyGeo = new THREE.ConeGeometry(0.8, 3.5, 8);
      bodyGeo.rotateX(Math.PI / 2);
      const bodyMat = new THREE.MeshBasicMaterial({ color: 0xFFF0D0 });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      group.add(body);

      // Main Wings
      const wingGeo = new THREE.BoxGeometry(4.2, 0.15, 1.0);
      const wingMat = new THREE.MeshBasicMaterial({ color: 0xA88756 });
      const wings = new THREE.Mesh(wingGeo, wingMat);
      group.add(wings);

      // Tail Fin
      const tailGeo = new THREE.BoxGeometry(0.15, 1.2, 0.6);
      const tailMat = new THREE.MeshBasicMaterial({ color: 0xA88756 });
      const tail = new THREE.Mesh(tailGeo, tailMat);
      tail.position.set(0, 0.6, -1.2);
      group.add(tail);

      group.scale.set(0.38, 0.38, 0.38);
      return group;
    };

    const initGlobe = () => {
      if (!containerRef.current || !window.Globe || worldRef.current) return;

      const globeWidth = 380;
      const globeHeight = 380;

      // Initialize flight objects array
      const initialFlightObjects = flightRoutes.map((route, i) => ({
        id: i,
        route,
        lat: route.startLat,
        lng: route.startLng,
        alt: 0.05
      }));

      const world = window.Globe()(containerRef.current)
        .width(globeWidth)
        .height(globeHeight)
        .backgroundColor('rgba(0,0,0,0)')
        // Realistic High-Definition NASA Blue Marble Earth Surface Texture
        .globeImageUrl('//unpkg.com/three-globe/example/img/earth-blue-marble.jpg')
        .bumpImageUrl('//unpkg.com/three-globe/example/img/earth-topology.png')
        .atmosphereColor('#4B8BE8')
        .atmosphereAltitude(0.18)
        // Black HTML Labels with crisp contrast for Country Hubs
        .htmlElementsData(countryHubs)
        .htmlLat(d => d.lat)
        .htmlLng(d => d.lng)
        .htmlElement(d => {
          const el = document.createElement('div');
          el.innerHTML = `
            <div style="display: flex; align-items: center; gap: 4px; transform: translate(-50%, -50%); pointer-events: none; white-space: nowrap;">
              <div style="width: 6px; height: 6px; background-color: #A88756; border-radius: 50%; border: 1.2px solid #FFFFFF; box-shadow: 0 0 6px #A88756;"></div>
              <span style="font-family: 'Outfit', sans-serif; font-weight: 500; font-size: 10.8px; color: #A88756; text-shadow: 0 1px 4px #000000, 0 0 6px #000000, 0 0 2px #000000; letter-spacing: 0.3px;">${d.name}</span>
            </div>
          `;
          return el;
        })
        // Golden Pulsing Rings around each Country Hub
        .ringsData(countryHubs)
        .ringLat(d => d.lat)
        .ringLng(d => d.lng)
        .ringColor(() => '#A88756')
        .ringMaxRadius(6)
        .ringPropagationSpeed(3)
        .ringRepeatPeriod(1200)
        // Animated Trading Arcs with 40% Opacity and Small Dashes
        .arcsData(flightRoutes)
        .arcStartLat(d => d.startLat)
        .arcStartLng(d => d.startLng)
        .arcEndLat(d => d.endLat)
        .arcEndLng(d => d.endLng)
        .arcAltitude(0.22)
        .arcColor(() => ['rgba(168, 135, 86, 0.4)', 'rgba(255, 240, 208, 0.6)', 'rgba(168, 135, 86, 0.4)'])
        .arcStroke(0.9)
        .arcDashLength(0.12)
        .arcDashGap(0.08)
        .arcDashAnimateTime(1800)
        // Moving 3D Flight Objects along arcs
        .objectsData(initialFlightObjects)
        .objectLat(d => d.lat)
        .objectLng(d => d.lng)
        .objectAltitude(d => d.alt)
        .objectFacesSurface(false)
        .objectThreeObject(() => createPlaneMesh());

      const controls = world.controls();
      if (controls) {
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.9;
        controls.enableZoom = false;
        controls.enablePan = false;
      }

      worldRef.current = world;

      // Continuous flight animation and globe rotation loop
      const animateLoop = () => {
        if (worldRef.current) {
          const now = Date.now() / 4000;
          const updatedFlights = flightRoutes.map((route, i) => {
            const t = (now + i * 0.16) % 1;
            // Interpolate flight position along Great Circle trajectory
            const lat = route.startLat + (route.endLat - route.startLat) * t;
            let lng = route.startLng + (route.endLng - route.startLng) * t;
            if (Math.abs(route.endLng - route.startLng) > 180) {
              lng = route.startLng + ((route.endLng - route.startLng + 360) % 360) * t;
            }
            // Parabolic altitude arc peaking mid-flight
            const alt = 0.08 + 0.22 * Math.sin(t * Math.PI);
            return { id: i, route, lat, lng, alt };
          });

          worldRef.current.objectsData(updatedFlights);

          if (worldRef.current.controls) {
            const ctrl = worldRef.current.controls();
            ctrl.autoRotate = true;
            ctrl.autoRotateSpeed = 0.9;
            ctrl.update();
          }
        }
        animId = requestAnimationFrame(animateLoop);
      };
      animateLoop();
    };

    if (window.Globe) {
      initGlobe();
    } else {
      const script = document.createElement('script');
      script.src = '//unpkg.com/globe.gl';
      script.async = true;
      script.onload = () => {
        if (isMounted) initGlobe();
      };
      document.head.appendChild(script);
    }

    return () => {
      isMounted = false;
      if (animId) cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <div 
      style={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 auto 64px',
        transform: `scale(${scaleFactor})`,
        transition: 'transform 0.15s ease-out',
        width: '380px',
        height: '380px'
      }}
    >
      <div 
        ref={containerRef} 
        style={{ width: '380px', height: '380px' }} 
      />
    </div>
  );
};

export default GlobeAnimation;
